import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  MessageCircle, 
  Phone, 
  CheckCircle, 
  AlertCircle,
  RefreshCw,
  Clock,
  User,
  Bot,
  ChevronDown,
  ChevronUp,
  ThumbsUp,
  ThumbsDown,
  Settings,
  Copy,
  ExternalLink,
  ClipboardCheck,
  XCircle,
  CheckCircle2,
  Truck,
  MapPin,
  Play,
  Square,
  Bell,
  Route
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WhatsAppPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [overrideHistory, setOverrideHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('status');
  const [expandedLog, setExpandedLog] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({ logId: null, feedback: '', correctResponse: '' });
  
  // Active trips state
  const [activeTrips, setActiveTrips] = useState([]);
  const [tripHistory, setTripHistory] = useState([]);
  const [startTripDialogOpen, setStartTripDialogOpen] = useState(false);
  const [newTripForm, setNewTripForm] = useState({ driver_phone: '', driver_name: '', origin: '', destination: '' });
  const [savingTrip, setSavingTrip] = useState(false);
  const [checkingConditions, setCheckingConditions] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, logsRes, convsRes, overrideRes, tripsRes, tripHistoryRes] = await Promise.all([
        axios.get(`${API}/whatsapp/status`),
        axios.get(`${API}/whatsapp/logs?limit=50&unreviewed_only=false`),
        axios.get(`${API}/whatsapp/conversations?limit=20`),
        axios.get(`${API}/override-requests`),
        axios.get(`${API}/trips/active`),
        axios.get(`${API}/trips/history?limit=20`)
      ]);
      setStatus(statusRes.data);
      setLogs(logsRes.data.logs);
      setConversations(convsRes.data.conversations);
      setOverrideHistory(overrideRes.data.requests || []);
      setActiveTrips(tripsRes.data.trips || []);
      setTripHistory(tripHistoryRes.data.trips || []);
    } catch (error) {
      console.error('Failed to fetch WhatsApp data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startTrip = async () => {
    if (!newTripForm.driver_phone || !newTripForm.origin || !newTripForm.destination) {
      toast.error('Please fill in phone, origin, and destination');
      return;
    }
    
    try {
      setSavingTrip(true);
      await axios.post(`${API}/trips/start`, newTripForm);
      toast.success('Trip started successfully');
      setStartTripDialogOpen(false);
      setNewTripForm({ driver_phone: '', driver_name: '', origin: '', destination: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trip');
    } finally {
      setSavingTrip(false);
    }
  };

  const endTrip = async (tripId) => {
    if (!window.confirm('End this trip?')) return;
    
    try {
      await axios.put(`${API}/trips/${tripId}/end`);
      toast.success('Trip ended');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end trip');
    }
  };

  const checkConditions = async () => {
    try {
      setCheckingConditions(true);
      const response = await axios.post(`${API}/trips/check-conditions`);
      const { trips_checked, alerts_sent } = response.data;
      
      if (alerts_sent > 0) {
        toast.success(`Checked ${trips_checked} trips, sent ${alerts_sent} alerts`);
      } else {
        toast.info(`Checked ${trips_checked} trips, no alerts needed`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check conditions');
    } finally {
      setCheckingConditions(false);
    }
  };

  const formatDuration = (startedAt) => {
    const started = new Date(startedAt);
    const now = new Date();
    const hours = (now - started) / (1000 * 60 * 60);
    if (hours < 1) {
      return `${Math.round(hours * 60)} min`;
    }
    return `${hours.toFixed(1)} hrs`;
  };

  const handleReviewLog = async (logId, approved) => {
    try {
      const formData = new FormData();
      if (feedbackForm.feedback) {
        formData.append('feedback', feedbackForm.feedback);
      }
      if (feedbackForm.correctResponse) {
        formData.append('correct_response', feedbackForm.correctResponse);
      }

      await axios.post(`${API}/whatsapp/logs/${logId}/review`, formData);
      toast.success('Log reviewed successfully');
      setFeedbackForm({ logId: null, feedback: '', correctResponse: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to review log');
    }
  };

  const copyWebhookUrl = () => {
    const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook`;
    navigator.clipboard.writeText(webhookUrl);
    toast.success('Webhook URL copied to clipboard');
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-CA', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground">WhatsApp Integration</h1>
          <p className="text-muted-foreground mt-1">
            Driver communication and knowledge base queries
          </p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('status')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'status' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Settings className="w-4 h-4 inline mr-2" />
          Setup & Status
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'logs' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <MessageCircle className="w-4 h-4 inline mr-2" />
          Message Logs
          {status?.stats?.unreviewed_messages > 0 && (
            <Badge variant="outline" className="ml-2 bg-primary/10 text-primary">
              {status.stats.unreviewed_messages}
            </Badge>
          )}
        </button>
        <button
          onClick={() => setActiveTab('conversations')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'conversations' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Phone className="w-4 h-4 inline mr-2" />
          Conversations
        </button>
        <button
          onClick={() => setActiveTab('overrides')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'overrides' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <ClipboardCheck className="w-4 h-4 inline mr-2" />
          Override Decisions
          {overrideHistory.filter(r => r.status !== 'pending').length > 0 && (
            <Badge variant="outline" className="ml-2 bg-secondary/50">
              {overrideHistory.filter(r => r.status !== 'pending').length}
            </Badge>
          )}
        </button>
        <button
          onClick={() => setActiveTab('trips')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'trips' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Truck className="w-4 h-4 inline mr-2" />
          Active Trips
          {activeTrips.length > 0 && (
            <Badge variant="default" className="ml-2 bg-emerald-500">
              {activeTrips.length}
            </Badge>
          )}
        </button>
      </div>

      {/* Status Tab */}
      {activeTab === 'status' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Integration Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {status?.configured ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-400" />
                )}
                Integration Status
              </CardTitle>
              <CardDescription>
                {status?.configured 
                  ? 'WhatsApp integration is active' 
                  : 'WhatsApp integration needs configuration'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <span className="text-sm text-muted-foreground">Twilio Account</span>
                  <Badge variant="outline" className={status?.twilio_account_set ? 'bg-emerald-500/10 text-emerald-400' : 'bg-yellow-500/10 text-yellow-400'}>
                    {status?.twilio_account_set ? 'Configured' : 'Not Set'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <span className="text-sm text-muted-foreground">WhatsApp Number</span>
                  <Badge variant="outline" className={status?.twilio_number_set ? 'bg-emerald-500/10 text-emerald-400' : 'bg-yellow-500/10 text-yellow-400'}>
                    {status?.twilio_number_set ? 'Configured' : 'Not Set'}
                  </Badge>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">{status?.stats?.total_conversations || 0}</p>
                  <p className="text-xs text-muted-foreground">Conversations</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">{status?.stats?.total_messages || 0}</p>
                  <p className="text-xs text-muted-foreground">Messages</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{status?.stats?.unreviewed_messages || 0}</p>
                  <p className="text-xs text-muted-foreground">To Review</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Setup Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Setup Instructions</CardTitle>
              <CardDescription>How to configure Twilio WhatsApp</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-medium">1</div>
                  <div>
                    <p className="text-sm font-medium">Get Twilio Account</p>
                    <p className="text-xs text-muted-foreground">Sign up at twilio.com and get your Account SID & Auth Token</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-medium">2</div>
                  <div>
                    <p className="text-sm font-medium">Enable WhatsApp Sandbox</p>
                    <p className="text-xs text-muted-foreground">Go to Messaging → Try it Out → WhatsApp</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-medium">3</div>
                  <div>
                    <p className="text-sm font-medium">Configure Webhook</p>
                    <p className="text-xs text-muted-foreground">Set the webhook URL in Twilio sandbox settings</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-medium">4</div>
                  <div>
                    <p className="text-sm font-medium">Add Credentials</p>
                    <p className="text-xs text-muted-foreground">Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_WHATSAPP_NUMBER to .env</p>
                  </div>
                </div>
              </div>

              {/* Webhook URL */}
              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Webhook URL:</p>
                <div className="flex gap-2">
                  <code className="flex-1 p-2 rounded bg-secondary text-xs text-foreground overflow-x-auto">
                    {process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook
                  </code>
                  <Button variant="outline" size="sm" onClick={copyWebhookUrl}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <Button variant="outline" className="w-full" asChild>
                <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Twilio Console
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Message Logs Tab */}
      {activeTab === 'logs' && (
        <div className="space-y-4">
          {logs.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium text-foreground">No messages yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Messages from drivers will appear here once WhatsApp is configured
                </p>
              </CardContent>
            </Card>
          ) : (
            logs.map((log) => (
              <Card key={log.id} className={!log.reviewed ? 'border-primary/50' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{log.phone_number}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(log.timestamp)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!log.reviewed && (
                        <Badge variant="outline" className="bg-primary/10 text-primary">
                          Needs Review
                        </Badge>
                      )}
                      <button
                        onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                        className="p-1 rounded hover:bg-secondary"
                      >
                        {expandedLog === log.id ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Message Preview */}
                  <div className="mt-4 space-y-3">
                    <div className="flex gap-3">
                      <User className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
                      <p className="text-sm text-foreground bg-secondary/50 rounded-lg p-3 flex-1">
                        {log.user_message}
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <Bot className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <p className="text-sm text-foreground bg-primary/10 rounded-lg p-3 flex-1">
                        {log.ai_response}
                      </p>
                    </div>
                  </div>

                  {/* Expanded View */}
                  {expandedLog === log.id && (
                    <div className="mt-4 pt-4 border-t border-border space-y-4">
                      {log.kb_context_used && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-2">Knowledge Base Context Used:</p>
                          <p className="text-xs text-muted-foreground bg-secondary/30 rounded p-2">
                            {log.kb_context_used}
                          </p>
                        </div>
                      )}

                      {!log.reviewed && (
                        <div className="space-y-3">
                          <Textarea
                            placeholder="Add feedback or correction (optional)"
                            value={feedbackForm.logId === log.id ? feedbackForm.feedback : ''}
                            onChange={(e) => setFeedbackForm({ ...feedbackForm, logId: log.id, feedback: e.target.value })}
                            rows={2}
                          />
                          <Textarea
                            placeholder="Provide correct response if AI was wrong (optional)"
                            value={feedbackForm.logId === log.id ? feedbackForm.correctResponse : ''}
                            onChange={(e) => setFeedbackForm({ ...feedbackForm, logId: log.id, correctResponse: e.target.value })}
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              className="flex-1"
                              onClick={() => handleReviewLog(log.id, true)}
                            >
                              <ThumbsUp className="w-4 h-4 mr-2" />
                              Mark Reviewed
                            </Button>
                          </div>
                        </div>
                      )}

                      {log.reviewed && log.manager_feedback && (
                        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                          <p className="text-xs font-medium text-emerald-400 mb-1">Manager Feedback:</p>
                          <p className="text-sm text-foreground">{log.manager_feedback}</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Conversations Tab */}
      {activeTab === 'conversations' && (
        <div className="space-y-4">
          {conversations.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Phone className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium text-foreground">No conversations yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Driver conversations will appear here
                </p>
              </CardContent>
            </Card>
          ) : (
            conversations.map((conv) => (
              <Card key={conv.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Phone className="w-4 h-4 text-primary" />
                      {conv.phone_number}
                    </CardTitle>
                    <CardDescription>
                      {conv.messages?.length || 0} messages • {formatTime(conv.updated_at)}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {conv.messages?.slice(-6).map((msg, idx) => (
                      <div key={idx} className={`flex gap-2 ${msg.role === 'assistant' ? 'justify-start' : 'justify-end'}`}>
                        <div className={`max-w-[80%] p-2 rounded-lg text-sm ${
                          msg.role === 'assistant' 
                            ? 'bg-primary/10 text-foreground' 
                            : 'bg-secondary text-foreground'
                        }`}>
                          {msg.content}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Override Decisions Tab */}
      {activeTab === 'overrides' && (
        <div className="space-y-4">
          {overrideHistory.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <ClipboardCheck className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium text-foreground">No override decisions yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Override request decisions will be logged here
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-foreground">{overrideHistory.length}</p>
                    <p className="text-xs text-muted-foreground">Total Requests</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-400">
                      {overrideHistory.filter(r => r.status === 'approved').length}
                    </p>
                    <p className="text-xs text-muted-foreground">Approved</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-red-400">
                      {overrideHistory.filter(r => r.status === 'denied').length}
                    </p>
                    <p className="text-xs text-muted-foreground">Denied</p>
                  </CardContent>
                </Card>
              </div>

              {/* Decision List */}
              {overrideHistory.map((request) => (
                <Card key={request.id} className={`border-l-4 ${
                  request.status === 'approved' ? 'border-l-emerald-500' : 
                  request.status === 'denied' ? 'border-l-red-500' : 'border-l-yellow-500'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {request.status === 'approved' ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                          ) : request.status === 'denied' ? (
                            <XCircle className="w-5 h-5 text-red-400" />
                          ) : (
                            <Clock className="w-5 h-5 text-yellow-400" />
                          )}
                          <Badge variant={
                            request.status === 'approved' ? 'default' : 
                            request.status === 'denied' ? 'destructive' : 'secondary'
                          } className={
                            request.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 
                            request.status === 'denied' ? 'bg-red-500/20 text-red-400 border-red-500/30' : ''
                          }>
                            {request.status.toUpperCase()}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {request.responded_at ? formatTime(request.responded_at) : formatTime(request.created_at)}
                          </span>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground">
                              {request.route_from} → {request.route_to}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Phone className="w-3 h-3" />
                            <span>{request.driver_phone}</span>
                            {request.driver_name && (
                              <>
                                <span>•</span>
                                <User className="w-3 h-3" />
                                <span>{request.driver_name}</span>
                              </>
                            )}
                          </div>

                          <div className="text-sm text-muted-foreground bg-secondary/30 rounded p-2">
                            <span className="font-medium">Reason:</span> {request.reason}
                          </div>

                          {request.response_note && (
                            <div className={`text-sm rounded p-2 ${
                              request.status === 'approved' 
                                ? 'bg-emerald-500/10 text-emerald-300' 
                                : 'bg-red-500/10 text-red-300'
                            }`}>
                              <span className="font-medium">Manager Note:</span> {request.response_note}
                            </div>
                          )}

                          {request.status !== 'pending' && (
                            <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                              <span>Request created: {formatTime(request.created_at)}</span>
                              {request.responded_at && (
                                <span className="ml-4">
                                  Decision made: {formatTime(request.responded_at)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </div>
      )}

      {/* Active Trips Tab */}
      {activeTab === 'trips' && (
        <div className="space-y-6">
          {/* Actions Bar */}
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setStartTripDialogOpen(true)}>
              <Play className="w-4 h-4 mr-2" />
              Start New Trip
            </Button>
            <Button 
              variant="outline" 
              onClick={checkConditions}
              disabled={checkingConditions || activeTrips.length === 0}
            >
              {checkingConditions ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Bell className="w-4 h-4 mr-2" />
              )}
              Check Conditions & Send Alerts
            </Button>
            <Button variant="ghost" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Active Trips */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-emerald-400" />
                Active Trips ({activeTrips.length})
              </CardTitle>
              <CardDescription>
                Drivers currently on the road. They will receive alerts if conditions change.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activeTrips.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No active trips</p>
                  <p className="text-sm">Drivers can start trips via WhatsApp: &quot;Starting trip from Calgary to Edmonton&quot;</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activeTrips.map((trip) => {
                    const traffic = trip.last_check?.traffic || trip.initial_conditions?.traffic;
                    const alertCount = trip.alerts_sent?.length || 0;
                    
                    return (
                      <div 
                        key={trip.id} 
                        className="p-4 rounded-lg border border-border bg-secondary/20 hover:bg-secondary/30 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <Route className="w-4 h-4 text-primary" />
                              <span className="font-medium">{trip.origin} → {trip.destination}</span>
                            </div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Phone className="w-3 h-3" />
                                <span>{trip.driver_phone}</span>
                                {trip.driver_name && <span className="text-xs">({trip.driver_name})</span>}
                              </div>
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                <span>Driving: {formatDuration(trip.started_at)}</span>
                              </div>
                              {traffic && (
                                <div className="flex items-center gap-2">
                                  <span>{traffic.traffic_emoji}</span>
                                  <span className={`text-sm ${
                                    traffic.traffic_severity === 'severe' ? 'text-red-400' :
                                    traffic.traffic_severity === 'heavy' ? 'text-orange-400' :
                                    traffic.traffic_severity === 'moderate' ? 'text-yellow-400' :
                                    'text-emerald-400'
                                  }`}>
                                    {traffic.traffic_severity?.toUpperCase()} - {traffic.formatted_time}
                                  </span>
                                </div>
                              )}
                              {alertCount > 0 && (
                                <div className="flex items-center gap-2">
                                  <Bell className="w-3 h-3 text-orange-400" />
                                  <span className="text-orange-400">{alertCount} alert(s) sent</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => endTrip(trip.id)}
                          >
                            <Square className="w-3 h-3 mr-1" />
                            End Trip
                          </Button>
                        </div>
                        
                        {/* Show alerts if any */}
                        {trip.alerts_sent?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-border/50">
                            <p className="text-xs text-muted-foreground mb-2">Recent Alerts:</p>
                            <div className="space-y-1">
                              {trip.alerts_sent.slice(-3).map((alert, idx) => (
                                <div key={idx} className="text-xs bg-orange-500/10 rounded px-2 py-1 text-orange-300">
                                  {alert.type}: {alert.message?.substring(0, 80)}...
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Trip History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Trip History</CardTitle>
              <CardDescription>Completed and cancelled trips</CardDescription>
            </CardHeader>
            <CardContent>
              {tripHistory.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No trip history yet</p>
              ) : (
                <div className="space-y-2">
                  {tripHistory.slice(0, 10).map((trip) => (
                    <div 
                      key={trip.id}
                      className="flex items-center justify-between p-3 rounded border border-border/50 text-sm"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant={trip.status === 'completed' ? 'default' : 'secondary'}>
                          {trip.status}
                        </Badge>
                        <span>{trip.origin} → {trip.destination}</span>
                        <span className="text-muted-foreground">({trip.driver_phone})</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(trip.completed_at || trip.started_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Start Trip Dialog */}
      <Dialog open={startTripDialogOpen} onOpenChange={setStartTripDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-primary" />
              Start New Trip
            </DialogTitle>
            <DialogDescription>
              Start tracking a driver trip. They will receive alerts if conditions change.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Driver Phone *</Label>
                <Input
                  placeholder="+1234567890"
                  value={newTripForm.driver_phone}
                  onChange={(e) => setNewTripForm({ ...newTripForm, driver_phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Driver Name</Label>
                <Input
                  placeholder="Optional"
                  value={newTripForm.driver_name}
                  onChange={(e) => setNewTripForm({ ...newTripForm, driver_name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Origin *</Label>
              <Input
                placeholder="e.g., Calgary, AB"
                value={newTripForm.origin}
                onChange={(e) => setNewTripForm({ ...newTripForm, origin: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Destination *</Label>
              <Input
                placeholder="e.g., Grande Prairie, AB"
                value={newTripForm.destination}
                onChange={(e) => setNewTripForm({ ...newTripForm, destination: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setStartTripDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={startTrip}
                disabled={savingTrip || !newTripForm.driver_phone || !newTripForm.origin || !newTripForm.destination}
              >
                {savingTrip ? 'Starting...' : 'Start Trip'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
